import { GoogleGenerativeAI } from "@google/generative-ai";

let genAI;
let chatSession;

function initializeAI() {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_AI_KEY;
    if (!apiKey) {
      console.error("Google AI API key is not defined in environment variables");
      return null;
    }
    console.log("Initializing Gemini AI with key:", apiKey.substring(0, 5) + "...");
    
    // Add more detailed logging
    if (apiKey.startsWith("AIza")) {
      console.warn("Warning: You might be using a Google Cloud API key. For Gemini, you need a key from https://makersuite.google.com/app/apikey");
    }
    
    const ai = new GoogleGenerativeAI(apiKey, {
      apiVersion: "v1",  // Changed from v1beta to v1
      timeout: 30000,    // 30 seconds timeout
    });
    console.log("Gemini AI initialized successfully");
    return ai;
  } catch (error) {
    console.error("Error initializing Gemini AI:", {
      error: error.message,
      stack: error.stack,
      type: error.name
    });
    return null;
  }
}

genAI = initializeAI();

export const chatService = {
  async sendMessage(message, chat = null, retryCount = 0, onStream) {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second

    try {
      if (!genAI) {
        throw new Error("Google AI not initialized. Please check your API key.");
      }

      if (!message?.trim()) {
        throw new Error("Message cannot be empty");
      }

      // Use existing chat session or create new one
      const currentChat = chat || chatSession;
      if (!currentChat) {
        const newChat = await this.startChat();
        chatSession = newChat;
        return this.streamResponse(newChat, message, onStream);
      }

      try {
        return this.streamResponse(currentChat, message, onStream);
      } catch (error) {
        // Handle network errors with retries
        if ((error.message?.includes('network') || error.message?.includes('timeout')) && retryCount < MAX_RETRIES) {
          console.log(`Retrying message send (attempt ${retryCount + 1} of ${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return this.sendMessage(message, chat, retryCount + 1);
        }
        throw error;
      }
    } catch (error) {
      console.error("Error in chat service:", {
        message: error.message,
        type: error.name,
        stack: error.stack,
      });

      // Map error messages to user-friendly versions
      if (error.message?.includes('API key')) {
        throw new Error("Service is temporarily unavailable. Please try again later.");
      } else if (error.message?.includes('quota')) {
        throw new Error("Service is busy. Please try again in a few minutes.");
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        throw new Error("Network error. Please check your internet connection and try again.");
      } else if (error.message?.includes('content filtered')) {
        throw new Error("Your message could not be processed. Please rephrase and try again.");
      } else {
        throw new Error(error.message || "Failed to get response. Please try again.");
      }
    }
  },

  async startChat(retryCount = 0) {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second

    try {
      if (!genAI) {
        // Try to initialize AI again in case it failed on first try
        genAI = initializeAI();
        if (!genAI) {
          throw new Error("Could not initialize Google AI. Please check your API key.");
        }
      }

      try {
        console.log("Creating chat model...");
        const model = genAI.getGenerativeModel({ 
          model: "gemini-2.5-flash-preview-04-17",
          generationConfig: {
            temperature: 0.7,
            candidateCount: 1,
            maxOutputTokens: 1000,
            stopSequences: [],
            streamingEnabled: true
          }
        });
        
        console.log("Starting chat session...");
        const chat = await model.startChat({
          history: [
            {
              role: "user",
              parts: [{ text: "You are a helpful campus management support assistant. Keep your responses friendly, concise and focused on campus-related topics." }]
            },
            {
              role: "model",
              parts: [{ text: "I understand that I am a campus management support assistant. I'll help with campus-related questions and issues." }]
            }
          ],
          generationConfig: {
            temperature: 0.7,
            candidateCount: 1,
            maxOutputTokens: 1000,
            stopSequences: []
          }
        });

        chatSession = chat;
        return chat;
      } catch (error) {
        // Handle network errors with retries
        if ((error.message?.includes('network') || error.message?.includes('timeout')) && retryCount < MAX_RETRIES) {
          console.log(`Retrying chat initialization (attempt ${retryCount + 1} of ${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return this.startChat(retryCount + 1);
        }
        throw error;
      }
    } catch (error) {
      console.error("Error starting chat:", {
        message: error.message,
        type: error.name,
        stack: error.stack,
      });

      // Map error messages to user-friendly versions
      if (error.message?.includes('API key')) {
        throw new Error("Service is temporarily unavailable. Please try again later.");
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        throw new Error("Network error. Please check your internet connection and try again.");
      } else {
        throw new Error("Failed to initialize chat. Please refresh the page and try again.");
      }
    }
  },

  async streamResponse(chat, message, onStream) {
    try {
      let fullResponse = '';
      const response = await chat.sendMessageStream(message);
      
      for await (const chunk of response.stream) {
        const chunkText = chunk.text();
        fullResponse += chunkText;
        if (onStream) {
          onStream(chunkText, false); // false indicates not done
        }
      }
      
      if (onStream) {
        onStream(fullResponse, true); // true indicates done
      }
      return fullResponse;
    } catch (error) {
      console.error('Stream error:', error);
      throw error;
    }
  },

  getCurrentChat() {
    return chatSession;
  },

  clearChat() {
    chatSession = null;
  }
};
